import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Camera, CheckCircle2, Clock, MapPin } from "lucide-react";

/**
 * The Hazri Book check-in screen.
 *
 * Carried over from the previous site. The three chips at the bottom are
 * the product's actual claim — person, place, time captured at the punch —
 * and they are deliberately shown as *captured inputs*, not as guarantees.
 * The product allows a punch through with a missing photo or location and
 * flags it, because discarding it would delete the evidence that someone
 * was on site. The site has stated the stronger, untrue version of this
 * once before; it does not do so again.
 */
export function HazriCheckIn() {
  const reduce = useReducedMotion();

  return (
    <div className="relative">
      <div className="glow-edge mx-auto w-full max-w-[300px] rounded-[2.5rem] border border-white/10 bg-ink-900/80 p-3 shadow-glow backdrop-blur-xl">
        <div className="rounded-[2rem] bg-[#0c1428] p-5">
          <div className="flex items-center justify-between text-[11px]">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/15 px-2 py-0.5 font-medium text-emerald-300">
              <span className="size-1.5 rounded-full bg-emerald-400" />
              Online
            </span>
            <span className="text-white/40">Today</span>
          </div>

          <div className="mt-6 text-center">
            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber-300/90">
              Not checked in yet
            </div>

            <motion.div
              animate={reduce ? undefined : { scale: [1, 1.03, 1] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              className="mx-auto mt-5 grid size-40 place-items-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-glow"
            >
              <div className="text-center">
                <ArrowUpRight className="mx-auto size-9" />
                <div className="mt-1 font-display text-2xl font-bold">IN</div>
              </div>
            </motion.div>

            <div className="mt-4 text-[11px] uppercase tracking-[0.14em] text-white/40">
              Tap to check in
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2 border-t border-white/5 pt-4">
            <Chip icon={Camera} label="Photo" />
            <Chip icon={MapPin} label="GPS" />
            <Chip icon={Clock} label="Time" />
          </div>
        </div>
      </div>

      {/* Sits below the phone in normal flow, not floating over it.
       *  Absolutely positioned it covered the Photo / GPS / Time chips —
       *  which are the product's actual claim — and the overlap got worse
       *  wherever the mock is scaled down. */}
      <motion.div
        animate={reduce ? undefined : { y: [0, -4, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="mx-auto mt-4 w-full max-w-[300px] rounded-2xl border border-white/10 bg-white/[0.06] p-3 shadow-glow backdrop-blur-xl"
      >
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-emerald-300">
          <CheckCircle2 className="size-3" />
          Anything missing is flagged
        </div>
        <div className="mt-1 text-xs text-white/80">
          Never silently dropped from the record
        </div>
      </motion.div>
    </div>
  );
}

function Chip({ icon: Icon, label }: { icon: typeof Camera; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-lg border border-white/5 bg-white/[0.03] py-2 text-white/70">
      <Icon className="size-4 text-emerald-300" />
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
    </div>
  );
}
